// ** Type Imports
import { Palette } from '@mui/material'
import { Skin } from 'src/layouts/types'

export interface CustomPalette extends Palette {
  navigationMenu: {
    background: string
    textColor: string
    hover: string
    selected: string
    active: string
  }

  primary: {
    light: string
    main: string
    dark: string
    contrastText: string
    hover: string
  }
  UI: {
    main: string
    light: string
    text: {
      info: string
    }
  }
  vars: {
    buttonGreenOpacity?: string
  }
}

// Dark mode inverts the grey ramp: grey.900 stays "the strongest text colour"
// and grey.50 stays "the faintest fill", so the hundreds of `grey.N` usages
// keep their meaning. Never use grey as a backdrop under a fixed-colour text.
const greyLight = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#000000',
  A100: '#F5F5F5',
  A200: '#EEEEEE',
  A400: '#BDBDBD',
  A700: '#616161',
}

const greyDark = {
  50: '#1A222C',
  100: '#222B36',
  200: '#2C3643',
  300: '#3C4654',
  400: '#5B6675',
  500: '#7C8798',
  600: '#9AA4B2',
  700: '#B8C0CB',
  800: '#D4DAE2',
  900: '#E6EBF0',
  A100: '#222B36',
  A200: '#2C3643',
  A400: '#5B6675',
  A700: '#B8C0CB',
}

const DefaultPalette = (
  mode: CustomPalette['mode'],
  skin: Skin
): CustomPalette => {
  // ** Vars
  const isLight = mode === 'light'
  const whiteColor = '#FFF'
  const lightColor = '47, 43, 61'
  const darkColor = '226, 232, 240'
  const lightPaperBgColor = whiteColor
  const darkPaperBgColor = '#161E27'
  const lightBodyBg = '#F7F8FB'
  const darkBodyBg = '#0F151C'
  const mainColor = isLight ? lightColor : darkColor

  const defaultBgColor = () => {
    if (skin === 'bordered') {
      return isLight ? lightPaperBgColor : darkPaperBgColor
    }

    return isLight ? lightBodyBg : darkBodyBg
  }

  return {
    navigationMenu: {
      background: isLight ? '#FFFFFF' : darkPaperBgColor,
      textColor: isLight ? '#24323D' : '#E6EBF0',
      hover: isLight ? 'rgba(36, 50, 61, 0.055)' : 'rgba(230, 235, 240, 0.06)',
      selected: isLight
        ? 'rgba(36, 50, 61, 0.10)'
        : 'rgba(230, 235, 240, 0.12)',
      active: isLight ? 'rgba(11, 214, 141, 0.10)' : 'rgba(11, 214, 141, 0.14)',
    },
    customColors: {
      dark: darkColor,
      main: mainColor,
      light: lightColor,
      lightPaperBg: lightPaperBgColor,
      darkPaperBg: darkPaperBgColor,
      bodyBg: isLight ? lightBodyBg : darkBodyBg, // Same as palette.background.default but doesn't consider bordered skin
      trackBg: isLight ? '#F1F0F2' : '#222B36',
      avatarBg: isLight ? '#1ABE71' : '#2A3441',
      tableHeaderBg: isLight ? '#F7F7FA' : '#1B242E',
      rowSelectedBg: isLight ? '#EDFFF8' : '#153030',
      rowSelectedHoverBg: isLight ? '#DCFAEF' : '#163B36',
    },
    mode: mode,

    common: {
      black: '#000',
      white: whiteColor,
    },
    primary: {
      light: '#0BD68D',
      main: '#0BD68D',
      dark: '#0BD68D',
      hover: '#029A63',
      contrastText: whiteColor,
    },
    secondary: {
      light: '#B2B4B8',
      main: '#A8AAAE',
      dark: '#949699',
      contrastText: whiteColor,
    },
    error: {
      light: '#ED6F70',
      main: '#EA5455',
      dark: '#CE4A4B',
      contrastText: whiteColor,
    },
    warning: {
      light: isLight ? '#fff5eb' : '#372F28',
      main: '#FF9F43',
      dark: '#E08C3B',
      contrastText: whiteColor,
    },
    info: {
      light: isLight ? '#0D6EFD' : '#60A5FA',
      main: isLight ? '#0D6EFD' : '#3D8BFD',
      dark: '#0D6EFD',
      contrastText: whiteColor,
    },
    success: {
      light: '#42CE80',
      main: '#0BD68D',
      dark: '#23AF62',
      contrastText: whiteColor,
    },

    grey: isLight ? greyLight : greyDark,
    text: {
      primary: isLight ? '#1A1A1A' : '#E6EBF0',
      secondary: isLight ? '#77778A' : '#9AA4B2',
      disabled: `rgba(${mainColor}, 0.42)`,
    },
    UI: {
      light: isLight ? '#EEEDFD' : '#262B48',
      main: isLight ? '#1F203A' : '#C7C9F2',
      text: {
        info: isLight ? '#0D6EFD' : '#3D8BFD',
      },
    },
    divider: `rgba(${mainColor}, 0.10)`,
    background: {
      paper: isLight ? lightPaperBgColor : darkPaperBgColor,
      default: defaultBgColor(),
    },
    action: {
      active: `rgba(${mainColor}, 0.54)`,
      hover: `rgba(${mainColor}, ${isLight ? 0.04 : 0.06})`,
      selected: `rgba(${mainColor}, ${isLight ? 0.06 : 0.1})`,
      selectedOpacity: isLight ? 0.06 : 0.1,
      disabled: `rgba(${mainColor}, 0.26)`,
      disabledBackground: `rgba(${mainColor}, 0.12)`,
      focus: `rgba(${mainColor}, 0.12)`,
    },
  } as CustomPalette
}

export default DefaultPalette
